import { firstValueFrom } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { PackagingLevelsDto } from '../core-data/packaging-levels-dto';
import { SectionTypeDto } from '../core-data/doc/section-type-dto';
import { EvidenceTypeDto } from '../core-data/doc/evidence-type-dto';
import { CheckboxFieldTypeDto } from '../core-data/doc/checkbox-field-type-dto';
import { environment } from 'src/environments/environment';
import { RadioGroupTypeDto } from '../core-data/doc/radio-group-type-dto';
import { CountryDto } from '../entities/country-dto';
import { EvidenceMethodTypeDto } from '../core-data/doc/evidence-method-type-dto';

@Injectable({ providedIn: 'root' })
export class CoreDataUpdateService {
  private _allPackagingLevels: PackagingLevelsDto[];
  private _allDOCSectionTypes: SectionTypeDto[];
  private _allDOCEvidenceTypes: EvidenceTypeDto[];
  private _allDOCEvidenceMethodTypes: EvidenceMethodTypeDto[];
  private _allDOCCheckboxFieldTypes: CheckboxFieldTypeDto[];
  private _allDOCRadioGroupTypes: RadioGroupTypeDto[];
  private _allCountries: CountryDto[] = [];
  private _allManufacturingCountries: CountryDto[] = [];
  private _allDistributionCountries: CountryDto[] = [];

  private _allPackagingLevelsMap = new Map<string, PackagingLevelsDto>();
  private _allDOCEvidenceTypesMap = new Map<string, EvidenceTypeDto>();
  private _allDOCEvidenceMethodTypesMap = new Map<string, EvidenceMethodTypeDto>();
  private _allDOCCheckboxFieldTypesMap = new Map<string, CheckboxFieldTypeDto>();
  private _allDOCSectionTypesMap = new Map<string, SectionTypeDto>();
  private _allDOCRadioGroupTypesMap = new Map<string, RadioGroupTypeDto>();
  private _allCountriesMap = new Map<string, CountryDto>();
  private _allManufacturingCountriesMap = new Map<string, CountryDto>();
  private _allDistributionCountriesMap = new Map<string, CountryDto>();

  constructor(private http: HttpClient) {
    this._allPackagingLevels = [];
    this._allDOCSectionTypes = [];
    this._allDOCEvidenceTypes = [];
    this._allDOCEvidenceMethodTypes = [];
    this._allDOCCheckboxFieldTypes = [];
    this._allDOCRadioGroupTypes = [];
  }

  get allPackagingLevels(): PackagingLevelsDto[] {
    return this._allPackagingLevels;
  }

  get allDOCSectionTypes(): SectionTypeDto[] {
    return this._allDOCSectionTypes;
  }

  get allDOCEvidenceTypes(): EvidenceTypeDto[] {
    return this._allDOCEvidenceTypes;
  }

  get allDOCEvidenceMethodTypes(): EvidenceMethodTypeDto[] {
    return this._allDOCEvidenceMethodTypes;
  }

  get allDOCCheckboxFieldTypes(): CheckboxFieldTypeDto[] {
    return this._allDOCCheckboxFieldTypes;
  }

  get allDOCRadioGroupTypes(): RadioGroupTypeDto[] {
    return this._allDOCRadioGroupTypes;
  }

  get allCountries(): CountryDto[] {
    return this._allCountries;
  }

  get allManufacturingCountries(): CountryDto[] {
    return this._allManufacturingCountries;
  }

  get allDistributionCountries(): CountryDto[] {
    return this._allDistributionCountries;
  }

  get allPackagingLevelsMap(): Map<string, PackagingLevelsDto> {
    return this._allPackagingLevelsMap;
  }

  get allDOCSectionTypesMap(): Map<string, SectionTypeDto> {
    return this._allDOCSectionTypesMap;
  }

  get allDOCEvidenceTypesMap(): Map<string, EvidenceTypeDto> {
    return this._allDOCEvidenceTypesMap;
  }

  get allDOCEvidenceMethodTypesMap(): Map<string, EvidenceMethodTypeDto> {
    return this._allDOCEvidenceMethodTypesMap;
  }

  get allDOCCheckboxFieldTypesMap(): Map<string, CheckboxFieldTypeDto> {
    return this._allDOCCheckboxFieldTypesMap;
  }

  get allDOCRadioGroupTypesMap(): Map<string, RadioGroupTypeDto> {
    return this._allDOCRadioGroupTypesMap;
  }

  get allCountriesMap(): Map<string, CountryDto> {
    return this._allCountriesMap;
  }

  get allManufacturingCountriesMap(): Map<string, CountryDto> {
    return this._allManufacturingCountriesMap;
  }

  get allDistributionCountriesMap(): Map<string, CountryDto> {
    return this._allDistributionCountriesMap;
  }

  public async updateCoreData() {
    this._allPackagingLevels = await this.getAllPackagingLevels();
    this._allDOCSectionTypes = await this.getAllDOCSectionTypes();
    this._allDOCEvidenceTypes = await this.getAllDOCEvidenceTypes();
    this._allDOCEvidenceMethodTypes = await this.getAllDOCEvidenceMethodTypes();
    this._allDOCCheckboxFieldTypes = await this.getAllDOCCheckboxFieldTypes();
    this._allDOCRadioGroupTypes = await this.getAllDOCRadioGroupTypes();
    this._allCountries = await firstValueFrom(this.http.get<CountryDto[]>(`${environment.webApiAddress}/api/countries`));
    this._allManufacturingCountries = await firstValueFrom(this.http.get<CountryDto[]>(`${environment.webApiAddress}/api/countries/manufacturing-countries`));
    this._allDistributionCountries = await firstValueFrom(this.http.get<CountryDto[]>(`${environment.webApiAddress}/api/countries/distribution-countries`));

    this._allPackagingLevelsMap.clear();
    this._allDOCSectionTypesMap.clear();
    this._allDOCEvidenceTypesMap.clear();
    this._allDOCEvidenceMethodTypesMap.clear();
    this._allDOCCheckboxFieldTypesMap.clear();
    this._allDOCRadioGroupTypesMap.clear();
    this._allCountriesMap.clear();
    this._allManufacturingCountriesMap.clear();
    this._allDistributionCountriesMap.clear();

    for (const levels of this._allPackagingLevels) {
      if (!this._allPackagingLevelsMap.has(levels.id)) {
        this._allPackagingLevelsMap.set(levels.id, levels);
      }
    }
    for (const section of this._allDOCSectionTypes) {
      if (!this._allDOCSectionTypesMap.has(section.id)) {
        this._allDOCSectionTypesMap.set(section.id, section);
      }
    }
    for (const evidence of this._allDOCEvidenceTypes) {
      if (!this._allDOCEvidenceTypesMap.has(evidence.id)) {
        this._allDOCEvidenceTypesMap.set(evidence.id, evidence);
      }
    }
    for (const evidenceMethod of this._allDOCEvidenceMethodTypes) {
      if (!this._allDOCEvidenceMethodTypesMap.has(evidenceMethod.id)) {
        this._allDOCEvidenceMethodTypesMap.set(evidenceMethod.id, evidenceMethod);
      }
    }
    for (const checkbox of this._allDOCCheckboxFieldTypes) {
      if (!this._allDOCCheckboxFieldTypesMap.has(checkbox.id)) {
        this._allDOCCheckboxFieldTypesMap.set(checkbox.id, checkbox);
      }
    }
    for (const radiogroup of this._allDOCRadioGroupTypes) {
      if (!this._allDOCRadioGroupTypesMap.has(radiogroup.id)) {
        this._allDOCRadioGroupTypesMap.set(radiogroup.id, radiogroup);
      }
    }

    for (const country of this._allCountries) {
      if (!this._allCountriesMap.has(country.futureId)) {
        this._allCountriesMap.set(country.futureId, country);
      }
    }

    for (const country of this._allManufacturingCountries) {
      if (!this._allManufacturingCountriesMap.has(country.futureId)) {
        this._allManufacturingCountriesMap.set(country.futureId, country);
      }
    }

    for (const country of this._allDistributionCountries) {
      if (!this._allDistributionCountriesMap.has(country.futureId)) {
        this._allDistributionCountriesMap.set(country.futureId, country);
      }
    }
  }

  public async getAllDOCSectionTypes(): Promise<SectionTypeDto[]> {
    const url = `${environment.webApiAddress}/api/doc/section-types`;
    return firstValueFrom(this.http.get<SectionTypeDto[]>(url));
  }

  public async getAllDOCEvidenceTypes(): Promise<EvidenceTypeDto[]> {
    const url = `${environment.webApiAddress}/api/doc/evidence-types`;
    return firstValueFrom(this.http.get<EvidenceTypeDto[]>(url));
  }

  public async getAllDOCEvidenceMethodTypes(): Promise<EvidenceMethodTypeDto[]> {
    const url = `${environment.webApiAddress}/api/doc/evidence-method-types`;
    return firstValueFrom(this.http.get<EvidenceMethodTypeDto[]>(url));
  }

  public async getAllDOCRadioGroupTypes(): Promise<RadioGroupTypeDto[]> {
    const url = `${environment.webApiAddress}/api/doc/radiogroup-types`;
    return firstValueFrom(this.http.get<RadioGroupTypeDto[]>(url));
  }

  public async getAllDOCCheckboxFieldTypes(): Promise<CheckboxFieldTypeDto[]> {
    const url = `${environment.webApiAddress}/api/doc/checkbox-types`;
    return firstValueFrom(this.http.get<CheckboxFieldTypeDto[]>(url));
  }

  public async getAllPackagingLevels(): Promise<PackagingLevelsDto[]> {
    const url = `${environment.webApiAddress}/api/packaging-levels`;
    return firstValueFrom(this.http.get<PackagingLevelsDto[]>(url));
  }
}
